import { useState, useCallback } from 'react';
import { searchDocumentCatalog } from '../../../services/api/DocumentCatalogService';

// 1ページあたりの件数は50件
const PER_PAGE = 50;

/**
 * 搭載文書検索の検索実行・結果管理フック
 *
 * API呼び出し、結果リスト・ページング情報・ローディング/エラー状態を管理する。
 * 1ページあたりの件数は50件固定（API仕様: デフォルト50、最大200）。
 *
 * @returns {Object} 検索結果の状態とハンドラ
 * @returns {Array} returns.items - 検索結果アイテム一覧（DocumentCatalogItem[]）
 * @returns {number} returns.total - 検索条件に合致する全件数
 * @returns {number} returns.page - 現在のページ番号（1始まり）
 * @returns {number} returns.totalPages - 総ページ数
 * @returns {boolean} returns.isLoading - 検索実行中フラグ
 * @returns {Error|null} returns.error - 検索エラー（nullの場合はエラーなし）
 * @returns {boolean} returns.hasSearched - 検索実行済みフラグ（EmptyState表示切替に使用）
 * @returns {Function} returns.executeSearch - 検索実行関数
 * @returns {Function} returns.resetResults - 検索結果リセット関数
 */
const useSearchResults = () => {
  /**
   * 検索結果アイテム一覧
   * APIの DocumentCatalogItem[] に対応する
   */
  const [items, setItems] = useState([]);

  /**
   * 検索条件に合致する全件数
   * ページャーの「XX件中」表示に使用
   */
  const [total, setTotal] = useState(0);

  /**
   * 現在のページ番号（1始まり）
   */
  const [page, setPage] = useState(1);

  /**
   * 総ページ数
   */
  const [totalPages, setTotalPages] = useState(0);

  /**
   * 検索実行中フラグ
   * trueの間は検索ボタンとリセットボタンを無効化する
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * 検索エラー
   * nullの場合はエラーなし
   */
  const [error, setError] = useState(null);

  /**
   * 検索実行済みフラグ
   * false: 初期状態（EmptyStateを表示）
   * true: 1回以上検索済み（結果0件の場合はNoResultsを表示）
   */
  const [hasSearched, setHasSearched] = useState(false);

  /**
   * 検索を実行する
   * per_page は 50 固定でAPIに送信する
   *
   * @param {Object} searchParams - buildSearchParams() で生成した検索条件
   * @param {number} [targetPage=1] - 取得するページ番号
   */
  const executeSearch = useCallback(async (searchParams, targetPage = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await searchDocumentCatalog({
        ...searchParams,
        page: targetPage,
        per_page: PER_PAGE,
      });
      setItems(response.items || []);
      setTotal(response.total || 0);
      setPage(response.page || targetPage);
      setTotalPages(response.total_pages || 0);
      setHasSearched(true);
    } catch (err) {
      if (err?.response?.status !== 400) {
        setError(err);
        setItems([]);
        setTotal(0);
        setTotalPages(0);
        setHasSearched(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 検索結果を初期状態にリセットする
   * リセットボタン押下時に useDocumentCatalogSearch から呼び出される
   */
  const resetResults = useCallback(() => {
    setItems([]);
    setTotal(0);
    setPage(1);
    setTotalPages(0);
    setHasSearched(false);
    setError(null);
  }, []);

  return {
    // 状態
    items,
    total,
    page,
    totalPages,
    isLoading,
    error,
    hasSearched,
    // ハンドラ
    executeSearch,
    resetResults,
  };
};

export default useSearchResults;
